import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useAttemptError } from "@modules/attempt/composables/use-attempt-error.ts";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant.js";
import { useStudyBuddy } from "@modules/study-buddy/composables/use-study-buddy.ts";
import { FROM_LOCATION } from "@modules/study-buddy/service/study.buddy.constant";
import { User } from "@plugins/richtext/plugins/mentions/types.ts";

interface AttemptMentionStoreUser extends Omit<User, "userUuid"> {
  userUuid?: string;
  uuid?: string;
}

interface AttemptMentionState {
  attemptContext?: unknown;
  studentList?: AttemptMentionStoreUser[];
  teacherList?: AttemptMentionStoreUser[];
}

const normalizeUserId = (userId: number | string | null | undefined): number | null => {
  const normalizedUserId = Number(userId);
  return Number.isNaN(normalizedUserId) ? null : normalizedUserId;
};

const getMentionableUsers = (
  users: AttemptMentionStoreUser[],
  authenticatedUserId: number | null,
): User[] => {
  const mapToMentionableUser = (user: AttemptMentionStoreUser): User | null => {
    const userUuid = user.userUuid ?? user.uuid;

    if (!userUuid) {
      return null;
    }

    return {
      ...user,
      userUuid,
    };
  };

  return users
    .filter((user) => user.userId !== authenticatedUserId)
    .map(mapToMentionableUser)
    .filter((user: User | null) => user !== null);
};

const toAssignmentErrorCode = (error: unknown): string => {
  return typeof error === "string" ? error : "";
};

export function useAttemptMention() {
  const store = useStore();
  const { showAssignmentError } = useAttemptError();
  const { fetchStudyBuddyListing } = useStudyBuddy(FROM_LOCATION.ASSIGNMENT);

  const attemptState = computed(() => store.state[NAMESPACE.ATTEMPT] as AttemptMentionState);
  const viewedUserId = ref<number | null>(null);

  const userId = computed<number | string | null>(
    () => store.getters[`${NAMESPACE.AUTH}/userId`] ?? null,
  );
  const attemptContext = computed(() => attemptState.value.attemptContext ?? null);
  const studentList = computed<AttemptMentionStoreUser[]>(
    () => attemptState.value.studentList ?? [],
  );
  const teacherList = computed<AttemptMentionStoreUser[]>(
    () => attemptState.value.teacherList ?? [],
  );
  const currentUserId = computed<number | string | null>(
    () => store.getters[`${NAMESPACE.ATTEMPT}/currentUserId`] ?? null,
  );
  const isTeacherView = computed<boolean>(
    () => store.getters[`${NAMESPACE.ATTEMPT}/isTeacherView`] ?? false,
  );
  const isStudentView = computed<boolean>(
    () => store.getters[`${NAMESPACE.ATTEMPT}/isStudentView`] ?? false,
  );
  const isTeacherStudentView = computed<boolean>(
    () => store.getters[`${NAMESPACE.ATTEMPT}/isTeacherStudentView`] ?? false,
  );
  const isAttemptableSession = computed<boolean>(
    () => store.getters[`${NAMESPACE.ATTEMPT}/isAttemptableSession`] ?? false,
  );
  const isFromAlsAdaptiveSection = computed<boolean>(
    () => store.getters[`${NAMESPACE.ATTEMPT}/isFromAlsAdaptiveSection`] ?? false,
  );
  const isSharingAssignmentEnabled = computed<boolean>(
    () => store.getters[`${NAMESPACE.ATTEMPT}/isSharingAssignmentEnabled`] ?? false,
  );
  const isStudentStudentView = computed<boolean>(
    () => store.getters[`${NAMESPACE.ATTEMPT}/isStudentStudentView`] ?? false,
  );
  const isAlsSection = computed<boolean>(
    () => isAttemptableSession.value || isFromAlsAdaptiveSection.value,
  );
  const authenticatedUserId = computed<number | null>(() => normalizeUserId(userId.value));

  const fetchTeacherList = (forceRefresh = false): Promise<unknown> => {
    return store.dispatch(`${NAMESPACE.ATTEMPT}/fetchTeacherList`, forceRefresh);
  };

  const fetchStudentList = (forceRefresh = false): Promise<unknown> => {
    return store.dispatch(`${NAMESPACE.ATTEMPT}/fetchStudentList`, forceRefresh);
  };

  const handleAssignmentError = async (error: unknown): void => {
    await showAssignmentError(toAssignmentErrorCode(error));
  };

  const getAttemptMentionableTeacherList = async (): Promise<User[]> => {
    if (isAlsSection.value) {
      return [];
    }

    try {
      await fetchTeacherList(false);
      return getMentionableUsers(teacherList.value, authenticatedUserId.value);
    } catch (error) {
      await handleAssignmentError(error);
      return [];
    }
  };

  const getAttemptMentionableStudentList = async (includeBuddyList = false): Promise<User[]> => {
    if (isAlsSection.value) {
      return [];
    }

    try {
      await fetchStudentList(false);

      if (isTeacherView.value) {
        return getMentionableUsers(studentList.value, authenticatedUserId.value);
      }

      if (isStudentView.value) {
        if (!isSharingAssignmentEnabled.value || !includeBuddyList) {
          return [];
        }

        try {
          const studyBuddies = await fetchStudyBuddyListing();
          const buddyListSet = new Set(studyBuddies.map((buddy) => buddy.userId));
          const buddiesInStudentList = studentList.value.filter(
            (student) => student.userId !== undefined && buddyListSet.has(student.userId),
          );

          return getMentionableUsers(buddiesInStudentList, authenticatedUserId.value);
        } catch (error) {
          await handleAssignmentError(error);
          return [];
        }
      }

      if (
        isTeacherStudentView.value ||
        (isStudentStudentView.value && isSharingAssignmentEnabled.value && includeBuddyList)
      ) {
        const viewingStudent = studentList.value.find(
          (student) => student.userId === normalizeUserId(currentUserId.value),
        );
        const usersToMention =
          viewedUserId.value !== authenticatedUserId.value && viewingStudent
            ? [viewingStudent]
            : [];

        return getMentionableUsers(usersToMention, authenticatedUserId.value);
      }

      return [];
    } catch (error) {
      await handleAssignmentError(error);
      return [];
    }
  };

  onMounted(() => {
    viewedUserId.value = normalizeUserId(currentUserId.value);
  });

  return {
    viewedUserId,
    userId,
    attemptContext,
    studentList,
    teacherList,
    currentUserId,
    isTeacherView,
    isStudentView,
    isTeacherStudentView,
    isAttemptableSession,
    isFromAlsAdaptiveSection,
    isSharingAssignmentEnabled,
    isStudentStudentView,
    isAlsSection,
    fetchTeacherList,
    fetchStudentList,
    getAttemptMentionableTeacherList,
    getAttemptMentionableStudentList,
  };
}
